import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, UserIcon, PhoneIcon, HomeIcon, CalendarIcon, ClockIcon, DollarSignIcon, MapPinIcon, EuroIcon, CheckCircleIcon, RefreshCwIcon, XCircleIcon, MessageSquareIcon, FileTextIcon, AlertCircleIcon, BuildingIcon, UploadIcon, FileCheckIcon, PenToolIcon, Edit2Icon, UserCheckIcon } from 'lucide-react';
import { ViewingCountdown } from './ViewingCountdown';
interface ViewingDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  viewing: any;
  onReschedule: (viewing: any) => void;
  onCancel: (viewing: any) => void;
  onComplete: (viewing: any) => void;
  onRescheduleSigning?: (viewing: any) => void;
  onMarkSignedInvoicing?: (viewing: any) => void;
  onCollectCommission?: (viewing: any) => void;
  onEdit?: (viewing: any) => void;
  onClientResponse?: (viewing: any) => void;
}
export function ViewingDetailsDrawer({
  isOpen,
  onClose,
  viewing,
  onReschedule,
  onCancel,
  onComplete,
  onRescheduleSigning,
  onMarkSignedInvoicing,
  onCollectCommission,
  onEdit,
  onClientResponse
}: ViewingDetailsDrawerProps) {
  if (!viewing) return null;
  const getStatusConfig = (status: string) => {
    const configs = {
      scheduled: {
        label: 'Programată',
        color: 'bg-blue-100 text-blue-700 border-blue-200',
        icon: CalendarIcon
      },
      rescheduled: {
        label: 'Reprogramată',
        color: 'bg-orange-100 text-orange-700 border-orange-200',
        icon: RefreshCwIcon
      },
      completed: {
        label: 'Finalizată',
        color: 'bg-slate-100 text-slate-700 border-slate-200',
        icon: CheckCircleIcon
      },
      cancelled: {
        label: 'Anulată',
        color: 'bg-red-100 text-red-700 border-red-200',
        icon: XCircleIcon
      },
      awaiting_response: {
        label: 'Aștept Răspuns',
        color: 'bg-blue-100 text-blue-700 border-blue-200',
        icon: ClockIcon
      },
      signing_scheduled: {
        label: 'Semnare Programată',
        color: 'bg-purple-100 text-purple-700 border-purple-200',
        icon: PenToolIcon
      },
      signed_invoicing: {
        label: 'Semnat - Facturare',
        color: 'bg-indigo-100 text-indigo-700 border-indigo-200',
        icon: FileTextIcon
      },
      commission_collected: {
        label: 'Comision Încasat',
        color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        icon: DollarSignIcon
      }
    };
    return configs[status as keyof typeof configs] || configs.scheduled;
  };
  const statusConfig = getStatusConfig(viewing.status);
  const StatusIcon = statusConfig.icon;
  const getCommissionBadge = () => {
    const config = {
      owner: {
        label: 'Proprietar',
        color: 'bg-purple-100 text-purple-700'
      },
      client: {
        label: 'Client',
        color: 'bg-blue-100 text-blue-700'
      },
      both: {
        label: 'Ambele',
        color: 'bg-orange-100 text-orange-700'
      }
    };
    const {
      label,
      color
    } = config[viewing.commissionPaidBy as keyof typeof config];
    let commissionText = label;
    if (viewing.commissionPaidBy === 'owner' && viewing.ownerCommission) {
      const vatText = viewing.ownerVatOption === 'additional' ? ' +TVA' : viewing.ownerVatOption === 'included' ? ' (TVA incl.)' : '';
      commissionText = `${label}: ${viewing.ownerCommission}%${vatText}`;
    } else if (viewing.commissionPaidBy === 'client' && viewing.clientCommission) {
      const vatText = viewing.clientVatOption === 'additional' ? ' +TVA' : viewing.clientVatOption === 'included' ? ' (TVA incl.)' : '';
      commissionText = `${label}: ${viewing.clientCommission}%${vatText}`;
    } else if (viewing.commissionPaidBy === 'both' && viewing.ownerCommission && viewing.clientCommission) {
      const ownerVatText = viewing.ownerVatOption === 'additional' ? '+TVA' : viewing.ownerVatOption === 'included' ? 'incl.' : '';
      const clientVatText = viewing.clientVatOption === 'additional' ? '+TVA' : viewing.clientVatOption === 'included' ? 'incl.' : '';
      commissionText = `P: ${viewing.ownerCommission}%${ownerVatText ? ' ' + ownerVatText : ''} / C: ${viewing.clientCommission}%${clientVatText ? ' ' + clientVatText : ''}`;
    }
    return <span className={`px-3 py-1 rounded-full text-sm font-bold ${color}`} data-id="element-5624">
        {commissionText}
      </span>;
  };
  return <AnimatePresence data-id="element-5625">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]" data-id="element-5626" />

          {/* Drawer */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed right-0 top-0 bottom-0 w-full sm:w-[600px] bg-white shadow-2xl z-[110] overflow-y-auto" data-id="element-5627">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-[#132C53] to-[#1e4278] text-white px-6 py-5 shadow-lg z-10" data-id="element-5628">
              <div className="flex items-center justify-between mb-4" data-id="element-5629">
                <div className="flex items-center space-x-3" data-id="element-5630">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm" data-id="element-5631">
                    <StatusIcon className="w-6 h-6" data-id="element-5632" />
                  </div>
                  <div data-id="element-5633">
                    <h3 className="text-xl font-bold" data-id="element-5634">Detalii Vizionare</h3>
                    <p className="text-sm text-blue-200" data-id="element-5635">{viewing.id}</p>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors" data-id="element-5636">
                  <XIcon className="w-6 h-6" data-id="element-5637" />
                </button>
              </div>

              {/* Status Badge */}
              <div className="flex items-center space-x-2" data-id="element-5638">
                <span className={`px-3 py-1.5 rounded-lg text-sm font-bold border-2 ${statusConfig.color}`} data-id="element-5639">
                  {statusConfig.label}
                </span>
                <span className="px-3 py-1.5 rounded-lg text-sm font-bold bg-blue-100 text-blue-700 border-2 border-blue-200" data-id="element-5640">
                  Închiriere
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6" data-id="element-5641">
              {/* Edit Button - Always visible at top */}
              {onEdit && <motion.button initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} onClick={() => {
            onEdit(viewing);
            onClose();
          }} className="w-full flex items-center justify-center space-x-2 p-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:shadow-lg hover:scale-105 transition-all font-bold" data-id="element-5642">
                  <Edit2Icon className="w-5 h-5" data-id="element-5643" />
                  <span data-id="element-5644">Editează Detalii Vizionare</span>
                </motion.button>}

              {/* Quick Actions - AWAITING RESPONSE */}
              {viewing?.status === 'awaiting_response' && onClientResponse && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200" data-id="element-5645">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-5646">
                    <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center" data-id="element-5647">
                      <UserCheckIcon className="w-5 h-5 text-white" data-id="element-5648" />
                    </div>
                    <div data-id="element-5649">
                      <h4 className="font-bold text-blue-900" data-id="element-5650">
                        Răspuns Client
                      </h4>
                      <p className="text-sm text-blue-700" data-id="element-5651">
                        Clientul s-a decis?
                      </p>
                    </div>
                  </div>
                  <button onClick={() => {
              onClientResponse(viewing);
              onClose();
            }} className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all" data-id="element-5652">
                    Actualizează Status Client
                  </button>
                </motion.div>}

              {/* Quick Actions - SCHEDULED/RESCHEDULED */}
              {(viewing.status === 'scheduled' || viewing.status === 'rescheduled') && <div className="grid grid-cols-3 gap-3" data-id="element-5653">
                  <button onClick={() => onReschedule(viewing)} className="flex flex-col items-center space-y-2 p-4 rounded-xl bg-orange-50 border-2 border-orange-200 hover:bg-orange-100 transition-colors" data-id="element-5654">
                    <RefreshCwIcon className="w-6 h-6 text-orange-600" data-id="element-5655" />
                    <span className="text-xs font-bold text-orange-900" data-id="element-5656">
                      Reprogramează
                    </span>
                  </button>
                  <button onClick={() => onComplete(viewing)} className="flex flex-col items-center space-y-2 p-4 rounded-xl bg-emerald-50 border-2 border-emerald-200 hover:bg-emerald-100 transition-colors" data-id="element-5657">
                    <MessageSquareIcon className="w-6 h-6 text-emerald-600" data-id="element-5658" />
                    <span className="text-xs font-bold text-emerald-900" data-id="element-5659">
                      Finalizează
                    </span>
                  </button>
                  <button onClick={() => onCancel(viewing)} className="flex flex-col items-center space-y-2 p-4 rounded-xl bg-red-50 border-2 border-red-200 hover:bg-red-100 transition-colors" data-id="element-5660">
                    <XCircleIcon className="w-6 h-6 text-red-600" data-id="element-5661" />
                    <span className="text-xs font-bold text-red-900" data-id="element-5662">
                      Anulează
                    </span>
                  </button>
                </div>}

              {/* Quick Actions - SIGNING_SCHEDULED */}
              {viewing.status === 'signing_scheduled' && onRescheduleSigning && onMarkSignedInvoicing && <div className="grid grid-cols-3 gap-3" data-id="element-5663">
                    <button onClick={() => onRescheduleSigning(viewing)} className="flex flex-col items-center space-y-2 p-4 rounded-xl bg-orange-50 border-2 border-orange-200 hover:bg-orange-100 transition-colors" data-id="element-5664">
                      <RefreshCwIcon className="w-6 h-6 text-orange-600" data-id="element-5665" />
                      <span className="text-xs font-bold text-orange-900" data-id="element-5666">
                        Reprogramează
                      </span>
                    </button>
                    <button onClick={() => onMarkSignedInvoicing(viewing)} className="flex flex-col items-center space-y-2 p-4 rounded-xl bg-emerald-50 border-2 border-emerald-200 hover:bg-emerald-100 transition-colors" data-id="element-5667">
                      <PenToolIcon className="w-6 h-6 text-emerald-600" data-id="element-5668" />
                      <span className="text-xs font-bold text-emerald-900" data-id="element-5669">
                        Semnat
                      </span>
                    </button>
                    <button onClick={() => onCancel(viewing)} className="flex flex-col items-center space-y-2 p-4 rounded-xl bg-red-50 border-2 border-red-200 hover:bg-red-100 transition-colors" data-id="element-5670">
                      <XCircleIcon className="w-6 h-6 text-red-600" data-id="element-5671" />
                      <span className="text-xs font-bold text-red-900" data-id="element-5672">
                        Anulează
                      </span>
                    </button>
                  </div>}

              {/* Quick Actions - SIGNED_INVOICING */}
              {viewing.status === 'signed_invoicing' && onCollectCommission && <div className="grid grid-cols-1 gap-3" data-id="element-5673">
                  <button onClick={() => onCollectCommission(viewing)} className="flex items-center justify-center space-x-3 p-4 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg transition-all" data-id="element-5674">
                    <DollarSignIcon className="w-6 h-6" data-id="element-5675" />
                    <span className="text-sm font-bold" data-id="element-5676">
                      Marchează Comision Încasat
                    </span>
                  </button>
                </div>}

              {/* Signing Info Card */}
              {viewing.status === 'signing_scheduled' && viewing.signingDate && <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-purple-200" data-id="element-5677">
                    <h4 className="text-sm font-bold text-purple-900 mb-4 flex items-center" data-id="element-5678">
                      <PenToolIcon className="w-4 h-4 mr-2" data-id="element-5679" />
                      Programare Semnare
                    </h4>
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-5680">
                      <div data-id="element-5681">
                        <p className="text-xs text-purple-600 mb-1" data-id="element-5682">Data</p>
                        <p className="text-lg font-bold text-purple-900" data-id="element-5683">
                          {viewing.signingDate}
                        </p>
                      </div>
                      <div data-id="element-5684">
                        <p className="text-xs text-purple-600 mb-1" data-id="element-5685">Ora</p>
                        <p className="text-lg font-bold text-purple-900" data-id="element-5686">
                          {viewing.signingTime}
                        </p>
                      </div>
                    </div>
                    {viewing.signingLocation && <div className="flex items-center space-x-2 p-2 bg-white rounded-lg" data-id="element-5687">
                        <MapPinIcon className="w-4 h-4 text-purple-600" data-id="element-5688" />
                        <span className="text-sm font-bold text-purple-900" data-id="element-5689">
                          {viewing.signingLocation}
                        </span>
                      </div>}
                    {viewing.signingNotes && <div className="mt-3 pt-3 border-t border-purple-200" data-id="element-5690">
                        <p className="text-xs text-purple-700" data-id="element-5691">
                          {viewing.signingNotes}
                        </p>
                      </div>}
                  </div>}

              {/* Commission Collected Info */}
              {viewing.status === 'commission_collected' && <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-6 border-2 border-emerald-200" data-id="element-5692">
                  <h4 className="text-sm font-bold text-emerald-900 mb-4 flex items-center" data-id="element-5693">
                    <DollarSignIcon className="w-4 h-4 mr-2" data-id="element-5694" />
                    Comision Încasat
                  </h4>
                  <div className="space-y-3" data-id="element-5695">
                    {viewing.ownerCommissionAmount && <div className="flex items-center justify-between p-3 bg-white rounded-lg" data-id="element-5696">
                        <span className="text-sm text-emerald-700" data-id="element-5697">
                          Comision Proprietar
                        </span>
                        <span className="text-lg font-bold text-emerald-900" data-id="element-5698">
                          {viewing.ownerCommissionAmount.toFixed(2)} EUR
                        </span>
                      </div>}
                    {viewing.clientCommissionAmount && <div className="flex items-center justify-between p-3 bg-white rounded-lg" data-id="element-5699">
                        <span className="text-sm text-emerald-700" data-id="element-5700">
                          Comision Client
                        </span>
                        <span className="text-lg font-bold text-emerald-900" data-id="element-5701">
                          {viewing.clientCommissionAmount.toFixed(2)} EUR
                        </span>
                      </div>}
                    <div className="flex items-center justify-between p-3 bg-emerald-100 rounded-lg border-2 border-emerald-300" data-id="element-5702">
                      <span className="text-sm font-bold text-emerald-900" data-id="element-5703">
                        Total
                      </span>
                      <span className="text-2xl font-bold text-emerald-600" data-id="element-5704">
                        {viewing.totalCommission?.toFixed(2) || '0.00'} EUR
                      </span>
                    </div>
                    {viewing.commissionNotes && <div className="mt-3 pt-3 border-t border-emerald-200" data-id="element-5705">
                        <p className="text-xs text-emerald-700" data-id="element-5706">
                          {viewing.commissionNotes}
                        </p>
                      </div>}
                  </div>
                </div>}

              {/* Countdown Card */}
              {(viewing.status === 'scheduled' || viewing.status === 'rescheduled') && <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200" data-id="element-5707">
                  <h4 className="text-sm font-bold text-blue-900 mb-4 flex items-center" data-id="element-5708">
                    <ClockIcon className="w-4 h-4 mr-2" data-id="element-5709" />
                    Timp Rămas
                  </h4>
                  <div className="flex justify-center" data-id="element-5710">
                    <ViewingCountdown date={viewing.date} time={viewing.time} status={viewing.status} data-id="element-5711" />
                  </div>
                </div>}

              {/* Date & Time Card */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200" data-id="element-5712">
                <h4 className="text-sm font-bold text-blue-900 mb-4 flex items-center" data-id="element-5713">
                  <CalendarIcon className="w-4 h-4 mr-2" data-id="element-5714" />
                  Programare Vizionare
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-5715">
                  <div data-id="element-5716">
                    <p className="text-xs text-blue-600 mb-1" data-id="element-5717">Data</p>
                    <p className="text-lg font-bold text-blue-900" data-id="element-5718">
                      {viewing.date}
                    </p>
                  </div>
                  <div data-id="element-5719">
                    <p className="text-xs text-blue-600 mb-1" data-id="element-5720">Ora</p>
                    <p className="text-lg font-bold text-blue-900" data-id="element-5721">
                      {viewing.time}
                    </p>
                  </div>
                </div>
              </div>

              {/* Client Info */}
              <div className="bg-white rounded-2xl p-6 border-2 border-slate-200" data-id="element-5722">
                <h4 className="text-sm font-bold text-slate-900 mb-4 flex items-center" data-id="element-5723">
                  <UserIcon className="w-4 h-4 mr-2 text-blue-600" data-id="element-5724" />
                  Informații Client
                </h4>
                <div className="space-y-3" data-id="element-5725">
                  {viewing.clientIsExternal ? <>
                      <div className="flex items-center space-x-2 mb-3 p-2 bg-blue-50 rounded-lg" data-id="element-5726">
                        <BuildingIcon className="w-4 h-4 text-blue-600" data-id="element-5727" />
                        <span className="text-xs font-bold text-blue-900" data-id="element-5728">
                          Reprezentat de agenție externă
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-5729">
                        <span className="text-sm text-slate-600" data-id="element-5730">Agenție</span>
                        <span className="text-sm font-bold text-slate-900" data-id="element-5731">
                          {viewing.clientAgencyName}
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-5732">
                        <span className="text-sm text-slate-600" data-id="element-5733">Agent</span>
                        <span className="text-sm font-bold text-slate-900" data-id="element-5734">
                          {viewing.clientAgentName}
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-5735">
                        <span className="text-sm text-slate-600" data-id="element-5736">
                          Telefon Agent
                        </span>
                        <a href={`tel:${viewing.clientAgentPhone}`} className="text-sm font-bold text-blue-600 hover:text-blue-700 flex items-center space-x-1" data-id="element-5737">
                          <PhoneIcon className="w-3 h-3" data-id="element-5738" />
                          <span data-id="element-5739">{viewing.clientAgentPhone}</span>
                        </a>
                      </div>
                    </> : <>
                      <div className="flex items-center justify-between" data-id="element-5740">
                        <span className="text-sm text-slate-600" data-id="element-5741">ID Lead</span>
                        <span className="text-sm font-bold text-slate-900 font-mono" data-id="element-5742">
                          {viewing.leadId}
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-5743">
                        <span className="text-sm text-slate-600" data-id="element-5744">Nume</span>
                        <span className="text-sm font-bold text-slate-900" data-id="element-5745">
                          {viewing.clientName}
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-5746">
                        <span className="text-sm text-slate-600" data-id="element-5747">Telefon</span>
                        <a href={`tel:${viewing.clientPhone}`} className="text-sm font-bold text-blue-600 hover:text-blue-700 flex items-center space-x-1" data-id="element-5748">
                          <PhoneIcon className="w-3 h-3" data-id="element-5749" />
                          <span data-id="element-5750">{viewing.clientPhone}</span>
                        </a>
                      </div>
                    </>}
                </div>

                {/* Client Contract Status */}
                {(viewing.clientContractSigned || viewing.clientSignPhysically) && <div className="mt-4 pt-4 border-t border-slate-200" data-id="element-5751">
                    <h5 className="text-xs font-bold text-slate-600 mb-2" data-id="element-5752">
                      Contract Client
                    </h5>
                    {viewing.clientContractSigned && <div className="flex items-center space-x-2 p-2 bg-emerald-50 rounded-lg" data-id="element-5753">
                        <FileCheckIcon className="w-4 h-4 text-emerald-600" data-id="element-5754" />
                        <span className="text-xs font-bold text-emerald-900" data-id="element-5755">
                          Contract semnat și încărcat
                        </span>
                      </div>}
                    {viewing.clientSignPhysically && <div className="flex items-center space-x-2 p-2 bg-blue-50 rounded-lg" data-id="element-5756">
                        <FileTextIcon className="w-4 h-4 text-blue-600" data-id="element-5757" />
                        <span className="text-xs font-bold text-blue-900" data-id="element-5758">
                          Se va semna fizic la vizionare
                        </span>
                      </div>}
                  </div>}
              </div>

              {/* Owner Info */}
              <div className="bg-white rounded-2xl p-6 border-2 border-slate-200" data-id="element-5759">
                <h4 className="text-sm font-bold text-slate-900 mb-4 flex items-center" data-id="element-5760">
                  <UserIcon className="w-4 h-4 mr-2 text-purple-600" data-id="element-5761" />
                  Informații Proprietar
                </h4>
                <div className="space-y-3" data-id="element-5762">
                  {viewing.ownerIsExternal ? <>
                      <div className="flex items-center space-x-2 mb-3 p-2 bg-purple-50 rounded-lg" data-id="element-5763">
                        <BuildingIcon className="w-4 h-4 text-purple-600" data-id="element-5764" />
                        <span className="text-xs font-bold text-purple-900" data-id="element-5765">
                          Reprezentat de agenție externă
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-5766">
                        <span className="text-sm text-slate-600" data-id="element-5767">Agenție</span>
                        <span className="text-sm font-bold text-slate-900" data-id="element-5768">
                          {viewing.ownerAgencyName}
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-5769">
                        <span className="text-sm text-slate-600" data-id="element-5770">Agent</span>
                        <span className="text-sm font-bold text-slate-900" data-id="element-5771">
                          {viewing.ownerAgentName}
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-5772">
                        <span className="text-sm text-slate-600" data-id="element-5773">
                          Telefon Agent
                        </span>
                        <a href={`tel:${viewing.ownerAgentPhone}`} className="text-sm font-bold text-purple-600 hover:text-purple-700 flex items-center space-x-1" data-id="element-5774">
                          <PhoneIcon className="w-3 h-3" data-id="element-5775" />
                          <span data-id="element-5776">{viewing.ownerAgentPhone}</span>
                        </a>
                      </div>
                    </> : <>
                      {viewing.ownerId && <div className="flex items-center justify-between" data-id="element-5777">
                          <span className="text-sm text-slate-600" data-id="element-5778">
                            ID Proprietar
                          </span>
                          <span className="text-sm font-bold text-slate-900 font-mono" data-id="element-5779">
                            {viewing.ownerId}
                          </span>
                        </div>}
                      <div className="flex items-center justify-between" data-id="element-5780">
                        <span className="text-sm text-slate-600" data-id="element-5781">Nume</span>
                        <span className="text-sm font-bold text-slate-900" data-id="element-5782">
                          {viewing.ownerName}
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-5783">
                        <span className="text-sm text-slate-600" data-id="element-5784">Telefon</span>
                        <a href={`tel:${viewing.ownerPhone}`} className="text-sm font-bold text-purple-600 hover:text-purple-700 flex items-center space-x-1" data-id="element-5785">
                          <PhoneIcon className="w-3 h-3" data-id="element-5786" />
                          <span data-id="element-5787">{viewing.ownerPhone}</span>
                        </a>
                      </div>
                    </>}
                </div>

                {/* Owner Contract Status */}
                {(viewing.ownerContractSigned || viewing.ownerSignPhysically) && <div className="mt-4 pt-4 border-t border-slate-200" data-id="element-5788">
                    <h5 className="text-xs font-bold text-slate-600 mb-2" data-id="element-5789">
                      Contract Proprietar
                    </h5>
                    {viewing.ownerContractSigned && <div className="flex items-center space-x-2 p-2 bg-emerald-50 rounded-lg" data-id="element-5790">
                        <FileCheckIcon className="w-4 h-4 text-emerald-600" data-id="element-5791" />
                        <span className="text-xs font-bold text-emerald-900" data-id="element-5792">
                          Contract semnat și încărcat
                        </span>
                      </div>}
                    {viewing.ownerSignPhysically && <div className="flex items-center space-x-2 p-2 bg-purple-50 rounded-lg" data-id="element-5793">
                        <FileTextIcon className="w-4 h-4 text-purple-600" data-id="element-5794" />
                        <span className="text-xs font-bold text-purple-900" data-id="element-5795">
                          Se va semna fizic la vizionare
                        </span>
                      </div>}
                  </div>}
              </div>

              {/* Property Info */}
              <div className="bg-white rounded-2xl p-6 border-2 border-slate-200" data-id="element-5796">
                <h4 className="text-sm font-bold text-slate-900 mb-4 flex items-center" data-id="element-5797">
                  <HomeIcon className="w-4 h-4 mr-2 text-amber-600" data-id="element-5798" />
                  Informații Proprietate
                </h4>
                <div className="space-y-3" data-id="element-5799">
                  <div className="flex items-center justify-between" data-id="element-5800">
                    <span className="text-sm text-slate-600" data-id="element-5801">
                      ID Proprietate
                    </span>
                    <span className="text-sm font-bold text-slate-900 font-mono" data-id="element-5802">
                      {viewing.propertyId}
                    </span>
                  </div>
                  {viewing.propertyAddress && <div className="flex items-start justify-between" data-id="element-5803">
                      <span className="text-sm text-slate-600" data-id="element-5804">Adresă</span>
                      <span className="text-sm font-bold text-slate-900 text-right max-w-[60%]" data-id="element-5805">
                        {viewing.propertyAddress}
                      </span>
                    </div>}
                  {viewing.propertyPrice && <div className="flex items-center justify-between" data-id="element-5806">
                      <span className="text-sm text-slate-600" data-id="element-5807">Preț</span>
                      <span className="text-sm font-bold text-emerald-600 flex items-center space-x-1" data-id="element-5808">
                        <EuroIcon className="w-3 h-3" data-id="element-5809" />
                        <span data-id="element-5810">{viewing.propertyPrice}</span>
                      </span>
                    </div>}
                </div>
              </div>

              {/* Commission Info */}
              <div className="bg-white rounded-2xl p-6 border-2 border-slate-200" data-id="element-5811">
                <h4 className="text-sm font-bold text-slate-900 mb-4 flex items-center" data-id="element-5812">
                  <DollarSignIcon className="w-4 h-4 mr-2 text-emerald-600" data-id="element-5813" />
                  Comisionare
                </h4>
                <div className="space-y-3" data-id="element-5814">
                  <div className="flex items-center justify-between" data-id="element-5815">
                    <span className="text-sm text-slate-600" data-id="element-5816">Plătit de</span>
                    {getCommissionBadge()}
                  </div>

                  {/* Owner Commission Details */}
                  {(viewing.commissionPaidBy === 'owner' || viewing.commissionPaidBy === 'both') && viewing.ownerCommission && <div className="pt-3 border-t border-slate-200" data-id="element-5817">
                        <p className="text-xs font-bold text-slate-600 mb-2" data-id="element-5818">
                          Comision Proprietar
                        </p>
                        <div className="bg-purple-50 rounded-lg p-3 space-y-2" data-id="element-5819">
                          <div className="flex items-center justify-between" data-id="element-5820">
                            <span className="text-sm text-purple-700" data-id="element-5821">
                              Procent
                            </span>
                            <span className="text-sm font-bold text-purple-900" data-id="element-5822">
                              {viewing.ownerCommission}%
                            </span>
                          </div>
                          <div className="flex items-center justify-between" data-id="element-5823">
                            <span className="text-sm text-purple-700" data-id="element-5824">TVA</span>
                            <span className="text-sm font-bold text-purple-900" data-id="element-5825">
                              {viewing.ownerVatOption === 'included' && 'Inclus în procent'}
                              {viewing.ownerVatOption === 'additional' && 'Se adaugă (21%)'}
                            </span>
                          </div>
                          {viewing.ownerDiscountRequested && <div className="pt-2 border-t border-purple-200" data-id="element-5826">
                              <div className="flex items-center space-x-2 p-2 bg-amber-50 border border-amber-300 rounded-lg" data-id="element-5827">
                                <AlertCircleIcon className="w-4 h-4 text-amber-600" data-id="element-5828" />
                                <span className="text-xs font-bold text-amber-900" data-id="element-5829">
                                  Derogare discount solicitată
                                </span>
                              </div>
                            </div>}
                        </div>
                      </div>}

                  {/* Client Commission Details */}
                  {(viewing.commissionPaidBy === 'client' || viewing.commissionPaidBy === 'both') && viewing.clientCommission && <div className="pt-3 border-t border-slate-200" data-id="element-5830">
                        <p className="text-xs font-bold text-slate-600 mb-2" data-id="element-5831">
                          Comision Client
                        </p>
                        <div className="bg-blue-50 rounded-lg p-3 space-y-2" data-id="element-5832">
                          <div className="flex items-center justify-between" data-id="element-5833">
                            <span className="text-sm text-blue-700" data-id="element-5834">
                              Procent
                            </span>
                            <span className="text-sm font-bold text-blue-900" data-id="element-5835">
                              {viewing.clientCommission}%
                            </span>
                          </div>
                          <div className="flex items-center justify-between" data-id="element-5836">
                            <span className="text-sm text-blue-700" data-id="element-5837">TVA</span>
                            <span className="text-sm font-bold text-blue-900" data-id="element-5838">
                              {viewing.clientVatOption === 'included' && 'Inclus în procent'}
                              {viewing.clientVatOption === 'additional' && 'Se adaugă (21%)'}
                            </span>
                          </div>
                          {viewing.clientDiscountRequested && <div className="pt-2 border-t border-blue-200" data-id="element-5839">
                              <div className="flex items-center space-x-2 p-2 bg-amber-50 border border-amber-300 rounded-lg" data-id="element-5840">
                                <AlertCircleIcon className="w-4 h-4 text-amber-600" data-id="element-5841" />
                                <span className="text-xs font-bold text-amber-900" data-id="element-5842">
                                  Derogare discount solicitată
                                </span>
                              </div>
                            </div>}
                        </div>
                      </div>}
                </div>
              </div>

              {/* Notes */}
              {viewing.agentNotes && <div className="bg-slate-50 rounded-2xl p-6 border-2 border-slate-200" data-id="element-5843">
                  <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center" data-id="element-5844">
                    <FileTextIcon className="w-4 h-4 mr-2 text-slate-600" data-id="element-5845" />
                    Observații Agent
                  </h4>
                  <p className="text-sm text-slate-700 leading-relaxed" data-id="element-5846">
                    {viewing.agentNotes}
                  </p>
                </div>}

              {/* Completion Info */}
              {(viewing.status === 'completed' || viewing.status === 'awaiting_response' || viewing.status === 'signing_scheduled' || viewing.status === 'signed_invoicing' || viewing.status === 'commission_collected') && viewing.completionFeedback && <div className={`rounded-2xl p-6 border-2 ${viewing.clientLikes ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-id="element-5847">
                    <h4 className={`text-sm font-bold mb-3 flex items-center ${viewing.clientLikes ? 'text-emerald-900' : 'text-amber-900'}`} data-id="element-5848">
                      <CheckCircleIcon className="w-4 h-4 mr-2" data-id="element-5849" />
                      Feedback Vizionare
                    </h4>
                    <p className={`text-sm ${viewing.clientLikes ? 'text-emerald-800' : 'text-amber-800'}`} data-id="element-5850">
                      {viewing.completionFeedback}
                    </p>
                  </div>}

              {/* Cancel Info */}
              {viewing.status === 'cancelled' && viewing.cancelReason && <div className="bg-red-50 rounded-2xl p-6 border-2 border-red-200" data-id="element-5851">
                  <h4 className="text-sm font-bold text-red-900 mb-3 flex items-center" data-id="element-5852">
                    <AlertCircleIcon className="w-4 h-4 mr-2" data-id="element-5853" />
                    Motiv Anulare
                  </h4>
                  <p className="text-sm text-red-800" data-id="element-5854">{viewing.cancelReason}</p>
                </div>}

              {/* Reschedule Info */}
              {viewing.status === 'rescheduled' && viewing.rescheduleReason && <div className="bg-orange-50 rounded-2xl p-6 border-2 border-orange-200" data-id="element-5855">
                  <h4 className="text-sm font-bold text-orange-900 mb-3 flex items-center" data-id="element-5856">
                    <RefreshCwIcon className="w-4 h-4 mr-2" data-id="element-5857" />
                    Motiv Reprogramare
                  </h4>
                  <p className="text-sm text-orange-800" data-id="element-5858">
                    {viewing.rescheduleReason}
                  </p>
                </div>}

              {/* Timeline */}
              <div className="bg-white rounded-2xl p-6 border-2 border-slate-200" data-id="element-5859">
                <h4 className="text-sm font-bold text-slate-900 mb-4 flex items-center" data-id="element-5860">
                  <ClockIcon className="w-4 h-4 mr-2 text-slate-600" data-id="element-5861" />
                  Timeline
                </h4>
                <div className="space-y-3" data-id="element-5862">
                  <div className="flex items-start space-x-3" data-id="element-5863">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" data-id="element-5864"></div>
                    <div className="flex-1" data-id="element-5865">
                      <p className="text-sm font-bold text-slate-900" data-id="element-5866">
                        Vizionare creată
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-5867">
                        {new Date(viewing.createdAt).toLocaleString('ro-RO')}
                      </p>
                    </div>
                  </div>
                  {viewing.rescheduledAt && <div className="flex items-start space-x-3" data-id="element-5868">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mt-2" data-id="element-5869"></div>
                      <div className="flex-1" data-id="element-5870">
                        <p className="text-sm font-bold text-slate-900" data-id="element-5871">
                          Reprogramată
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-5872">
                          {new Date(viewing.rescheduledAt).toLocaleString('ro-RO')}
                        </p>
                      </div>
                    </div>}
                  {viewing.completedAt && <div className="flex items-start space-x-3" data-id="element-5873">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2" data-id="element-5874"></div>
                      <div className="flex-1" data-id="element-5875">
                        <p className="text-sm font-bold text-slate-900" data-id="element-5876">
                          Finalizată
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-5877">
                          {new Date(viewing.completedAt).toLocaleString('ro-RO')}
                        </p>
                      </div>
                    </div>}
                  {viewing.signingScheduledAt && <div className="flex items-start space-x-3" data-id="element-5878">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-2" data-id="element-5879"></div>
                      <div className="flex-1" data-id="element-5880">
                        <p className="text-sm font-bold text-slate-900" data-id="element-5881">
                          Semnare programată
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-5882">
                          {new Date(viewing.signingScheduledAt).toLocaleString('ro-RO')}
                        </p>
                      </div>
                    </div>}
                  {viewing.signedAt && <div className="flex items-start space-x-3" data-id="element-5883">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2" data-id="element-5884"></div>
                      <div className="flex-1" data-id="element-5885">
                        <p className="text-sm font-bold text-slate-900" data-id="element-5886">
                          Contract semnat
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-5887">
                          {new Date(viewing.signedAt).toLocaleString('ro-RO')}
                        </p>
                      </div>
                    </div>}
                  {viewing.commissionCollectedAt && <div className="flex items-start space-x-3" data-id="element-5888">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2" data-id="element-5889"></div>
                      <div className="flex-1" data-id="element-5890">
                        <p className="text-sm font-bold text-slate-900" data-id="element-5891">
                          Comision încasat
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-5892">
                          {new Date(viewing.commissionCollectedAt).toLocaleString('ro-RO')}
                        </p>
                      </div>
                    </div>}
                  {viewing.cancelledAt && <div className="flex items-start space-x-3" data-id="element-5893">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-2" data-id="element-5894"></div>
                      <div className="flex-1" data-id="element-5895">
                        <p className="text-sm font-bold text-slate-900" data-id="element-5896">
                          Anulată
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-5897">
                          {new Date(viewing.cancelledAt).toLocaleString('ro-RO')}
                        </p>
                      </div>
                    </div>}
                </div>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}